import {
  IconLoader2,
  IconPlus,
  IconCircleXFilled,
} from '@tabler/icons-react'
import {
  Upload,
  UploadProps,
  App,
  Spin,
} from 'antd'
import Image from 'next/image'
import React, {
  MouseEventHandler,
  useCallback,
  useState,
} from 'react'

import { axios } from '@/utils/axios'
import { getApiBaseUrl } from '@/utils/env'

import s from './avatar-uploader.module.css'

export interface AvatarUploaderProps {
  disabled?: boolean;
  value?: string
  onChange?: (value?: string) => void
}

export interface FileResponse {
  data: {
    name: string
    url: string
  }
}

export default function AvatarUploader({
  value,
  onChange,
  disabled,
}: AvatarUploaderProps) {
  const [loading, setLoading] = useState(false)
  const { message } = App.useApp()

  const handleChange: NonNullable<UploadProps<FileResponse>['onChange']> = useCallback(({
    file: {
      status,
      response,
    },
  }) => {
    if (status === 'uploading') {
      setLoading(true)
      return
    }
    if (status === 'done' && onChange) {
      onChange(response?.data.url)
      setLoading(false)
      return
    }
    if (status === 'error') {
      setLoading(false)
      void message.error('Upload failed.')
    }
  }, [message, onChange])

  const handleRemove: MouseEventHandler<SVGSVGElement> = useCallback((event) => {
    event.stopPropagation()
    if (onChange) {
      onChange(undefined)
    }
  }, [onChange])

  const customRequest: NonNullable<UploadProps['customRequest']> = useCallback(async ({
    onSuccess,
    onError,
    file,
    onProgress,
  }) => {
    try {
      const formData = new FormData()
      formData.append('file', file)
      if (onProgress) {
        onProgress({ percent: 0 })
      }
      const response = await axios.post<FileResponse>(
        `${getApiBaseUrl()}file`,
        formData,
        {
          headers: { 'content-type': 'multipart/form-data' },
          onUploadProgress: ({ loaded, total = 1 }) => {
            if (onProgress) {
              onProgress({ percent: (loaded / total) * 100 })
            }
          },
        },
      )
      if (onSuccess) {
        onSuccess(response)
      }
    } catch (e) {
      if (onError) {
        console.error(e)
        onError(new Error('Upload error'))
      }
    }
  }, [])

  return (
    <Upload<FileResponse>
      listType="picture-card"
      showUploadList={false}
      action={`${getApiBaseUrl()}file`}
      customRequest={customRequest}
      onChange={handleChange}
      accept="image/*"
      className={s.Upload}
      disabled={disabled}
    >
      {value ? (
        <Spin spinning={loading} wrapperClassName={s.UploadedWrapper}>
          <Image
            className={s.UploadedImage}
            src={value}
            alt="avatar"
            width={100}
            height={100}
          />
          {!disabled && <IconCircleXFilled className={s.Remove} onClick={handleRemove} />}
        </Spin>
      ) : (
        <button type="button" disabled={disabled} className={s.UploadButton}>
          {loading ? <IconLoader2 className="anticon-spin" /> : <IconPlus />}
          <div className={s.UploadText}>{loading ? 'Uploading...' : 'Upload'}</div>
        </button>
      )}
    </Upload>
  )
}
